import { useEffect, useRef, useState } from 'react';
import { useShellDrawer } from '../../provider';
import { AgentsSection } from './AgentsSection';
import { AllAgentsView } from './AllAgentsView';
import { AllConversationsView } from './AllConversationsView';
import { ConversationsSection } from './ConversationsSection';
import { DrawerHeader } from './DrawerHeader';
import { StudioLinkRow } from './StudioLinkRow';
import styles from './ShellDrawer.module.css';

interface DrawerBodyProps {
  readonly view: 'default' | 'all-agents' | 'all-conversations';
}

function DrawerBody({ view }: DrawerBodyProps) {
  if (view === 'all-agents') return <AllAgentsView />;
  if (view === 'all-conversations') return <AllConversationsView />;
  return (
    <>
      <AgentsSection />
      <ConversationsSection />
    </>
  );
}

/** How long the sheet takes to slide away before it unmounts. Matches the
 *  slideOut animation in ShellDrawer.module.css. */
const DRAWER_EXIT_MS = 200;

/** Overlay shell drawer rendered above `ChatPanel`. Combines AGENTS and
 *  RECENT sections in the default view; "Browse all agents" /
 *  "See all conversations" flip to expanded views in the same drawer.
 *  Slides in and out; Escape closes it, and focus moves into it on open. */
export function ShellDrawer() {
  const drawer = useShellDrawer();
  const sheetRef = useRef<HTMLElement>(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [wasOpen, setWasOpen] = useState(drawer.isOpen);

  if (drawer.isOpen !== wasOpen) {
    setWasOpen(drawer.isOpen);
    setIsLeaving(!drawer.isOpen);
  }

  useEffect(() => {
    if (!isLeaving) return;
    const timer = setTimeout(() => setIsLeaving(false), DRAWER_EXIT_MS);
    return () => clearTimeout(timer);
  }, [isLeaving]);

  const { isOpen, close } = drawer;
  // On open: remember what had focus, move focus into the sheet, keep Tab
  // inside it (it is modal), and let Escape close it. On close: hand focus
  // back to whatever opened it.
  useEffect(() => {
    if (!isOpen) return;
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const sheet = sheetRef.current;
    sheet?.focus({ preventScroll: true });

    // Escape belongs to the drawer while it is open. Stopping it here keeps
    // the host's own Escape-closes-the-panel listener (on window) from also
    // firing once this close has flushed.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key === 'Tab' && sheet) {
        trapTab(event, sheet);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus({ preventScroll: true });
    };
  }, [isOpen, close]);

  if (!isOpen && !isLeaving) return null;
  const state = isOpen ? 'open' : 'closing';

  return (
    <>
      <button
        type="button"
        className={styles.backdrop}
        data-state={state}
        aria-label="Close drawer"
        onClick={close}
        tabIndex={isOpen ? 0 : -1}
      />
      <aside
        ref={sheetRef}
        role="dialog"
        aria-label="Ask Moo"
        aria-modal="true"
        aria-hidden={!isOpen}
        inert={!isOpen}
        tabIndex={-1}
        className={styles.drawer}
        data-state={state}
      >
        <DrawerHeader />
        <StudioLinkRow />
        <DrawerBody view={drawer.view} />
      </aside>
    </>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** Keeps Tab and Shift+Tab cycling within the sheet. */
function trapTab(event: KeyboardEvent, sheet: HTMLElement) {
  const focusable = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE));
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === sheet)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}
